import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Field, LinkField, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import {
  getLocationParam,
  stripLocationFromRouter,
  withLocationParam,
} from 'lib/utils/locationSearchParam';
import {
  StSiteMasterBreadcrumb,
  StSiteMasterBreadcrumbCurrent,
  StSiteMasterBreadcrumbLink,
  StSiteMasterBreadcrumbText,
} from 'components/SiteMaster/SiteMaster.styles';
import { getFieldValue } from 'components/SiteMaster/utils';
import { showLoader } from 'src/features/loaderSlice';
import { resetSearch } from 'src/features/locationSearchSlice';

type SiteBreadcrumbProps = {
  label?: Field<string>;
  url?: LinkField;
  siteName?: Field<string>;
};

const SiteBreadcrumb = ({ label, url, siteName }: SiteBreadcrumbProps): JSX.Element | null => {
  const dispatch = useDispatch();
  const router = useRouter();
  // Read after mount: the page is statically generated, so the server markup
  // never has a query string. Empty on a cold start, which drops the crumb.
  const [locality, setLocality] = useState('');

  useEffect(() => {
    const nextLocality = getLocationParam();
    if (nextLocality) {
      setLocality(nextLocality);
    }
    // Keep the city crumb from React state; drop ?location= from router.query
    // so header/logo getQuery() cannot copy it onto /search-location later.
    stripLocationFromRouter(router);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot read + strip
  }, [router.isReady]);

  const searchHref = url?.value?.href ?? '';
  const hasSearchLink = Boolean(searchHref) && Boolean(getFieldValue(label));
  const siteNameValue = getFieldValue(siteName);
  const startNavigation = () => dispatch(showLoader());
  // Plain "Find a Site" has no ?location=; clear stale distance sort before leave.
  const startFreshSearch = () => {
    dispatch(resetSearch());
    dispatch(showLoader());
  };

  if (!locality && !siteNameValue) {
    return null;
  }

  return (
    <StSiteMasterBreadcrumb
      aria-label="Breadcrumb"
      separator={<ChevronRightIcon aria-hidden />}
      id="site-breadcrumb"
    >
      {hasSearchLink ? (
        <StSiteMasterBreadcrumbLink href={searchHref} onClick={startFreshSearch}>
          <Text field={label} />
        </StSiteMasterBreadcrumbLink>
      ) : null}

      {locality ? (
        hasSearchLink ? (
          <StSiteMasterBreadcrumbLink
            href={withLocationParam(searchHref, locality)}
            onClick={startNavigation}
          >
            {locality}
          </StSiteMasterBreadcrumbLink>
        ) : (
          <StSiteMasterBreadcrumbText>{locality}</StSiteMasterBreadcrumbText>
        )
      ) : null}

      {siteNameValue ? (
        <StSiteMasterBreadcrumbCurrent aria-current="page">
          <Text field={siteName} />
        </StSiteMasterBreadcrumbCurrent>
      ) : null}
    </StSiteMasterBreadcrumb>
  );
};

export default SiteBreadcrumb;
